import { FunctionComponent, useState } from 'react';
import cn from 'classnames';
import { useForm, FormProvider } from 'react-hook-form';
import { Person, Requirement } from '../../types';
import { Button, Section, SectionHeading } from '../../components';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { Renderer, formSpecToRendererConfig } from '../../components/forms';
import DefaultAnnualReportSpec from '../../components/forms/specs/annual_report_spec.json';
import { useScrollIntoView } from '../../hooks';
import { FormError } from '../../components/forms/FormError';

type AnnualReportProps = {
  locationId: string;
  requirement: Requirement;
  peopleData: Person[];
  onEnterLater: () => void;
  onSubmitSuccess: () => void;
  isFullWidth?: boolean;
  scrollOnMount?: boolean;
};

type AnnualReportIFormValue = {
  total_authorized_shares: number;
  total_issued_shares?: number;
  gross_assets?: number;
};

export const DefaultAnnualReport: FunctionComponent<AnnualReportProps> = ({
  locationId: rawLocationId,
  requirement,
  peopleData,
  onEnterLater,
  onSubmitSuccess,
  isFullWidth,
  scrollOnMount,
}) => {
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const locationId = rawLocationId.toLowerCase();

  const formMethods = useForm<AnnualReportIFormValue>();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: AnnualReportIFormValue) => {
    const {
      total_authorized_shares: totalAuthorizedShares,
      total_issued_shares: totalIssuedShares,
      gross_assets: grossAssets,
    } = data;

    setHasSubmitError(false);

    const payload = {
      legal_entity_requirement_id: requirement.id,
      authorized_shares: totalAuthorizedShares,
      ...(totalIssuedShares && { total_issued_shares: totalIssuedShares }),
      ...(grossAssets && { gross_assets: grossAssets }),
    };

    fetchApi({
      url: `/api/legal_entity/locations/${locationId}/annual_report`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (status === ApiStatus.Error) {
        setHasSubmitError(true);
      } else {
        onSubmitSuccess();
      }
    });
  };

  const loadedSpec = DefaultAnnualReportSpec;

  return (
    <Section className="flex flex-col items-center" ref={scrollIntoViewRef}>
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text={requirement.title || loadedSpec.title}
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Renderer
            config={formSpecToRendererConfig(loadedSpec)}
            errors={errors}
            peopleData={peopleData}
          />
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              <Button
                type="button"
                variant="grayTertiary"
                size="large"
                isFullWidth
                onClick={onEnterLater}
              >
                Enter later
              </Button>
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};
